import { useEffect, useState, type ReactNode } from 'react';
import { Dialog } from './Dialog';

/**
 * Promise-based replacements for window.confirm / window.prompt / window.alert.
 *
 * Named confirmDialog/promptDialog/alertDialog (NOT the native names) on
 * purpose: the app-wide cutover greps for the native window-dialog call tokens
 * and must return zero, so these replacements deliberately avoid those tokens.
 *
 * Usage:
 *   if (await confirmDialog({ title: 'Delete ticket?', tone: 'danger' })) { ... }
 *   const reason = await promptDialog({ title: 'Blocked reason', required: true });
 *   if (reason === null) return; // cancelled
 *
 * Mount <DialogHost /> exactly once near the app root (Layout). Requests are
 * shown one at a time (a small queue), mirroring the sequential semantics of
 * the native dialogs they replace.
 */

type Resolver<T> = (value: T) => void;

interface ConfirmRequest {
  kind: 'confirm';
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
  resolve: Resolver<boolean>;
}

interface PromptRequest {
  kind: 'prompt';
  title: string;
  label?: ReactNode;
  defaultValue?: string;
  placeholder?: string;
  multiline?: boolean;
  required?: boolean;
  confirmLabel?: string;
  resolve: Resolver<string | null>;
}

interface AlertRequest {
  kind: 'alert';
  title: string;
  body?: ReactNode;
  resolve: Resolver<void>;
}

type DialogRequest = ConfirmRequest | PromptRequest | AlertRequest;

const queue: DialogRequest[] = [];
let listener: (() => void) | null = null;

function notify() {
  listener?.();
}

function enqueue(req: DialogRequest) {
  queue.push(req);
  notify();
}

export function confirmDialog(opts: Omit<ConfirmRequest, 'kind' | 'resolve'>): Promise<boolean> {
  return new Promise((resolve) => enqueue({ kind: 'confirm', resolve, ...opts }));
}

export function promptDialog(opts: Omit<PromptRequest, 'kind' | 'resolve'>): Promise<string | null> {
  return new Promise((resolve) => enqueue({ kind: 'prompt', resolve, ...opts }));
}

export function alertDialog(opts: Omit<AlertRequest, 'kind' | 'resolve'>): Promise<void> {
  return new Promise((resolve) => enqueue({ kind: 'alert', resolve, ...opts }));
}

/** Mount once. Renders the head of the dialog queue. */
export function DialogHost() {
  const [, force] = useState(0);
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    listener = () => force((n) => n + 1);
    return () => {
      listener = null;
    };
  }, []);

  const current = queue[0];

  // Reset the prompt field whenever a new request reaches the head of the queue.
  useEffect(() => {
    if (current?.kind === 'prompt') {
      setValue(current.defaultValue ?? '');
      setTouched(false);
    }
  }, [current]);

  if (!current) return null;

  function finish(result: boolean | string | null | void) {
    const req = queue.shift();
    if (req) (req.resolve as Resolver<typeof result>)(result);
    // Re-render for the next queued request (or to unmount).
    force((n) => n + 1);
  }

  if (current.kind === 'alert') {
    return (
      <Dialog
        open
        onOpenChange={(o) => !o && finish(undefined)}
        title={current.title}
        footer={
          <button
            type="button"
            onClick={() => finish(undefined)}
            className="px-3 py-1.5 rounded-md bg-brand-600 text-white text-sm hover:bg-brand-700"
          >
            OK
          </button>
        }
      >
        {current.body && <p className="text-sm text-slate-700">{current.body}</p>}
      </Dialog>
    );
  }

  if (current.kind === 'confirm') {
    const danger = current.tone === 'danger';
    return (
      <Dialog
        open
        onOpenChange={(o) => !o && finish(false)}
        title={current.title}
        footer={
          <>
            <button
              type="button"
              onClick={() => finish(false)}
              className="px-3 py-1.5 rounded-md border border-slate-300 text-sm hover:bg-slate-50"
            >
              {current.cancelLabel ?? 'Cancel'}
            </button>
            <button
              type="button"
              onClick={() => finish(true)}
              className={`px-3 py-1.5 rounded-md text-white text-sm ${
                danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-brand-600 hover:bg-brand-700'
              }`}
            >
              {current.confirmLabel ?? (danger ? 'Delete' : 'Confirm')}
            </button>
          </>
        }
      >
        {current.body && <p className="text-sm text-slate-700">{current.body}</p>}
      </Dialog>
    );
  }

  // prompt
  const trimmed = value.trim();
  const invalid = current.required === true && trimmed.length === 0;
  const submit = () => {
    if (invalid) {
      setTouched(true);
      return;
    }
    finish(value);
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && finish(null)}
      title={current.title}
      footer={
        <>
          <button
            type="button"
            onClick={() => finish(null)}
            className="px-3 py-1.5 rounded-md border border-slate-300 text-sm hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={invalid}
            className="px-3 py-1.5 rounded-md bg-brand-600 text-white text-sm hover:bg-brand-700 disabled:opacity-60"
          >
            {current.confirmLabel ?? 'OK'}
          </button>
        </>
      }
    >
      {current.label && <label className="block text-sm text-slate-700">{current.label}</label>}
      {current.multiline ? (
        <textarea
          autoFocus
          rows={5}
          value={value}
          placeholder={current.placeholder}
          onChange={(e) => setValue(e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
        />
      ) : (
        <input
          autoFocus
          value={value}
          placeholder={current.placeholder}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
        />
      )}
      {invalid && touched && <p className="text-sm text-rose-600">This field is required.</p>}
    </Dialog>
  );
}
